import React, { useState, useEffect, useMemo } from 'react';
import { useUser } from "../../context/UserContext";
import {
    Card,
    Table,
    Select,
    Button,
    Tabs,
    Modal,
    Form,
    Input,
    InputNumber,
    message,
    Space,
    Popconfirm,
    Tag,
    DatePicker,
    Spin,
    Empty,
    Row,
    Col
} from "antd";
import {
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    UploadOutlined
} from "@ant-design/icons";
import apiClient from '../../services/apiClient';
import "./AdminPages.css";
import dayjs from 'dayjs';

const { Option } = Select;
const { TabPane } = Tabs;

const IssuerBorrowingAdmin = () => {
    const { userData } = useUser(); // Using context for token if needed, or localStorage directly
    const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:8080";
    const token = localStorage.getItem("token");

    const [issuerOptions, setIssuerOptions] = useState([]);
    const [selectedIssuer, setSelectedIssuer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [borrowingData, setBorrowingData] = useState({
        borrowings: [],
        borrowingDetails: {}, // Map<Type, List>
        ncds: { secured: [], unsecured: [] },
        commercialPapers: { secured: [], unsecured: [] }
    });

    // Modal States
    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState(null); // 'borrowing', 'details', 'ncd', 'cp'
    const [editingRecord, setEditingRecord] = useState(null);
    const [form] = Form.useForm();

    const authHeaders = useMemo(() => {
        const headers = {
            "Content-Type": "application/json",
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        return headers;
    }, [token]);

    useEffect(() => {
        fetchIssuerOptions();
    }, []);

    useEffect(() => {
        if (selectedIssuer) {
            fetchBorrowingData(selectedIssuer);
        } else {
            setBorrowingData({
                borrowings: [],
                borrowingDetails: {},
                ncds: { secured: [], unsecured: [] },
                commercialPapers: { secured: [], unsecured: [] }
            });
        }
    }, [selectedIssuer]);

    const fetchIssuerOptions = async () => {
        try {
            const response = await apiClient.get(
                `${baseUrl}/master-issuer/issuers/basic-industry`,
                { headers: authHeaders }
            );
            const result = response.data;
            if (result.statusCode === 200 && Array.isArray(result.data)) {
                setIssuerOptions(result.data);
                if (result.data.length > 0 && !selectedIssuer) {
                    setSelectedIssuer(result.data[0].issuer);
                }
            }
        } catch (error) {
            console.error("Error fetching issuers:", error);
            message.error("Failed to fetch issuers");
        }
    };

    const fetchBorrowingData = async (issuer) => {
        setLoading(true);
        try {
            const response = await apiClient.get(
                `${baseUrl}/borrowing/issuer/${encodeURIComponent(issuer)}`,
                { headers: authHeaders }
            );
            const result = response.data;
            if (result.statusCode === 200) {
                setBorrowingData(result.data);
            }
        } catch (error) {
            if (error.response?.status === 404) {
                setBorrowingData({
                    borrowings: [],
                    borrowingDetails: {},
                    ncds: { secured: [], unsecured: [] },
                    commercialPapers: { secured: [], unsecured: [] }
                });
            } else {
                console.error("Error fetching borrowing data:", error);
                message.error("Failed to fetch borrowing data");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleIssuerChange = (value) => {
        setSelectedIssuer(value);
    };

    // ================== CRUD HANDLERS ==================

    const handleDelete = async (type, id) => {
        try {
            let endpoint = '';
            let body = null;
            let method = 'DELETE';

            if (type === 'borrowing') {
                endpoint = `${baseUrl}/borrowing/borrowings`;
                body = JSON.stringify(id); // id is Composite Key object
            } else if (type === 'details') {
                endpoint = `${baseUrl}/borrowing/details/${id}`;
            } else if (type === 'ncd') {
                endpoint = `${baseUrl}/borrowing/ncds/${id}`;
            } else if (type === 'cp') {
                endpoint = `${baseUrl}/borrowing/commercial-papers/${id}`;
            }

            await apiClient.request({
                url: endpoint,
                method,
                headers: authHeaders,
                data: body
            });

            message.success("Deleted successfully");
            fetchBorrowingData(selectedIssuer);
        } catch (error) {
            console.error("Delete error:", error);
            message.error("Error deleting record");
        }
    };

    const openModal = (type, record = null) => {
        setModalType(type);
        setEditingRecord(record);
        setModalVisible(true);
        form.resetFields();
        if (record) {
            // Handle date fields if any
            form.setFieldsValue(record);
        } else {
            // Defaults
            if (type === 'borrowing') {
                form.setFieldsValue({ issuer: selectedIssuer });
            }
        }
    };



    // ================== RENDER HELPERS ==================

    // PDF Extraction Handler
    const [extractionLoading, setExtractionLoading] = useState(false);
    const pocBotsUrl = process.env.REACT_APP_POC_BOTS_URL || "http://localhost:5002";



    const renderGeneralBorrowings = () => {
        const columns = [
            { title: 'Security Type', dataIndex: ['id', 'borrowingSecurityType'], key: 'type' },
            {
                title: 'Term Loan (Banks)',
                dataIndex: 'termLoanFromBanks',
                key: 'termLoanFromBanks',
                render: (val) => val != null ? val.toLocaleString() : '-'
            },
            {
                title: 'Term Loan (Other Banks)',
                dataIndex: 'termLoanFromOtherBanks',
                key: 'termLoanFromOtherBanks',
                render: (val) => val != null ? val.toLocaleString() : '-'
            },
            {
                title: 'Term Loan (Fin. Inst.)',
                dataIndex: 'termLoanFromFinancialInstitutions',
                key: 'termLoanFromFinancialInstitutions',
                render: (val) => val != null ? val.toLocaleString() : '-'
            },
            {
                title: 'Term Loan (Foreign Banks)',
                dataIndex: 'termLoanFromForeignBanks',
                key: 'termLoanFromForeignBanks',
                render: (val) => val != null ? val.toLocaleString() : '-'
            },
            {
                title: 'Term Loan (Foreign Fin. Inst.)',
                dataIndex: 'termLoanFromForeignFinancialInstitutions',
                key: 'termLoanFromForeignFinancialInstitutions',
                render: (val) => val != null ? val.toLocaleString() : '-'
            },
            {
                title: 'ECB',
                dataIndex: 'externalCommercialBorrowingsEcb',
                key: 'externalCommercialBorrowingsEcb',
                render: (val) => val != null ? val.toLocaleString() : '-'
            },
            {
                title: 'Other Loans',
                dataIndex: 'otherLoans',
                key: 'otherLoans',
                render: (val) => val != null ? val.toLocaleString() : '-'
            },
            {
                title: 'Term Loan (NHB)',
                dataIndex: 'termLoanFromNhb',
                key: 'termLoanFromNhb',
                render: (val) => val != null ? val.toLocaleString() : '-'
            },
            {
                title: 'NCDs',
                dataIndex: 'nonConvertibleDebenturesNcds',
                key: 'nonConvertibleDebenturesNcds',
                render: (val) => val != null ? val.toLocaleString() : '-'
            },
            {
                title: 'Working Capital Loan',
                dataIndex: 'loansRepayableOnDemandWorkingCapitalDemandLoan',
                key: 'loansRepayableOnDemandWorkingCapitalDemandLoan',
                render: (val) => val != null ? val.toLocaleString() : '-'
            },
            {
                title: 'Loans Related Parties',
                dataIndex: 'loansFromRelatedParties',
                key: 'loansFromRelatedParties',
                render: (val) => val != null ? val.toLocaleString() : '-'
            },
            {
                title: 'Cash Credit',
                dataIndex: 'cashCreditFromBanks',
                key: 'cashCreditFromBanks',
                render: (val) => val != null ? val.toLocaleString() : '-'
            },
            {
                title: 'Bank Overdraft',
                dataIndex: 'bankOverdraft',
                key: 'bankOverdraft',
                render: (val) => val != null ? val.toLocaleString() : '-'
            },
            {
                title: 'Domestic Borrowings',
                dataIndex: 'borrowingsWithinIndia',
                key: 'borrowingsWithinIndia',
                render: (val) => val != null ? val.toLocaleString() : '-'
            },
            {
                title: 'Foreign Borrowings',
                dataIndex: 'borrowingsOutsideIndia',
                key: 'borrowingsOutsideIndia',
                render: (val) => val != null ? val.toLocaleString() : '-'
            },
            {
                title: 'Action',
                key: 'action',
                fixed: 'right',
                width: 100,
                render: (_, record) => (
                    <Space>
                        <Button icon={<EditOutlined />} onClick={() => openModal('borrowing', record)} />
                        <Popconfirm title="Delete?" onConfirm={() => handleDelete('borrowing', record.id)}>
                            <Button icon={<DeleteOutlined />} danger />
                        </Popconfirm>
                    </Space>
                )
            }
        ];
        return (
            <div>
                <Space style={{ marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal('borrowing')}>
                        Add General Summary
                    </Button>
                </Space>
                <Table
                    dataSource={borrowingData.borrowings}
                    columns={columns}
                    rowKey={(r) => r.id?.borrowingSecurityType}
                    scroll={{ x: 3000 }}
                />
            </div>
        );
    };

    const renderDetails = () => {
        // Flatten the map for the table
        let data = [];
        if (borrowingData.borrowingDetails) {
            Object.keys(borrowingData.borrowingDetails).forEach(type => {
                const group = borrowingData.borrowingDetails[type]; // { secured: [], unsecured: [] }
                // API response structure is slightly complex: Map<BorrowingType, SecuredUnsecuredListDTO>
                // But checking DTO, it is Map<BorrowingType, SecuredUnsecuredListDTO>
                // We need to handle this.
                if (group.secured) data = [...data, ...group.secured];
                if (group.unsecured) data = [...data, ...group.unsecured];
            });
        }

        const columns = [
            { title: 'Type', dataIndex: 'borrowingType', key: 'bType' },
            { title: 'Security', dataIndex: 'borrowingSecurityType', key: 'sType' },
            { title: 'Amount', dataIndex: 'totalAmount', key: 'amount', render: (val) => val?.toLocaleString() },
            { title: 'Tenure', dataIndex: 'tenure', key: 'tenure' },
            { title: 'Interest Rate', dataIndex: 'rateOfInterest', key: 'roi' },
            {
                title: 'Action',
                key: 'action',
                render: (_, record) => (
                    <Space>
                        <Button icon={<EditOutlined />} onClick={() => openModal('details', record)} />
                        <Popconfirm title="Delete?" onConfirm={() => handleDelete('details', record.id)}>
                            <Button icon={<DeleteOutlined />} danger />
                        </Popconfirm>
                    </Space>
                )
            }
        ];

        return (
            <div>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal('details')} style={{ marginBottom: 16 }}>
                    Add Detail Breakup
                </Button>
                <Table dataSource={data} columns={columns} rowKey="id" />
            </div>
        );
    };

    const renderInstruments = (type) => { // type = 'ncd' or 'cp'
        const dataObj = type === 'ncd' ? borrowingData.ncds : borrowingData.commercialPapers;
        const data = [...(dataObj?.secured || []), ...(dataObj?.unsecured || [])];

        const columns = [
            { title: 'Security', dataIndex: 'borrowingSecurityType', key: 'sType' },
            { title: 'Series', dataIndex: 'seriesDetails', key: 'series' },
            { title: 'Allotment Date', dataIndex: 'dateOfAllotment', key: 'date' },
            { title: 'Amount', dataIndex: 'amount', key: 'amnt', render: (val) => val?.toLocaleString() },
            { title: 'Interest Rate', dataIndex: 'interestRate', key: 'roi' },
            {
                title: 'Action',
                key: 'action',
                render: (_, record) => (
                    <Space>
                        <Button icon={<EditOutlined />} onClick={() => openModal(type, record)} />
                        <Popconfirm title="Delete?" onConfirm={() => handleDelete(type, record.id)}>
                            <Button icon={<DeleteOutlined />} danger />
                        </Popconfirm>
                    </Space>
                )
            }
        ];

        return (
            <div>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal(type)} style={{ marginBottom: 16 }}>
                    Add {type === 'ncd' ? 'NCD' : 'Commercial Paper'}
                </Button>
                <Table dataSource={data} columns={columns} rowKey="id" />
            </div>
        );
    };

    // ================== FORM CONTENT ==================
    const renderFormContent = () => {
        if (modalType === 'borrowing') {
            return (
                <>
                    <Form.Item name="borrowingSecurityType" label="Security Type" rules={[{ required: true }]}>
                        <Select>
                            <Option value="SECURED">SECURED</Option>
                            <Option value="UNSECURED">UNSECURED</Option>
                        </Select>
                    </Form.Item>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <Form.Item name="termLoanFromBanks" label="Term Loan (Banks)"><InputNumber style={{ width: '100%' }} /></Form.Item>
                        <Form.Item name="termLoanFromOtherBanks" label="Term Loan (Other Banks)"><InputNumber style={{ width: '100%' }} /></Form.Item>
                        <Form.Item name="termLoanFromFinancialInstitutions" label="Term Loan (Fin. Inst.)"><InputNumber style={{ width: '100%' }} /></Form.Item>
                        <Form.Item name="termLoanFromForeignBanks" label="Term Loan (Foreign Banks)"><InputNumber style={{ width: '100%' }} /></Form.Item>
                        <Form.Item name="termLoanFromForeignFinancialInstitutions" label="Term Loan (Foreign FI)"><InputNumber style={{ width: '100%' }} /></Form.Item>
                        <Form.Item name="externalCommercialBorrowingsEcb" label="ECB"><InputNumber style={{ width: '100%' }} /></Form.Item>
                        <Form.Item name="otherLoans" label="Other Loans"><InputNumber style={{ width: '100%' }} /></Form.Item>
                        <Form.Item name="termLoanFromNhb" label="Term Loan (NHB)"><InputNumber style={{ width: '100%' }} /></Form.Item>
                        <Form.Item name="nonConvertibleDebenturesNcds" label="NCDs"><InputNumber style={{ width: '100%' }} /></Form.Item>
                        <Form.Item name="loansRepayableOnDemandWorkingCapitalDemandLoan" label="Working Capital Loan"><InputNumber style={{ width: '100%' }} /></Form.Item>
                        <Form.Item name="loansFromRelatedParties" label="Loans Related Parties"><InputNumber style={{ width: '100%' }} /></Form.Item>
                        <Form.Item name="cashCreditFromBanks" label="Cash Credit"><InputNumber style={{ width: '100%' }} /></Form.Item>
                        <Form.Item name="bankOverdraft" label="Bank Overdraft"><InputNumber style={{ width: '100%' }} /></Form.Item>
                        <Form.Item name="borrowingsWithinIndia" label="Domestic Borrowings"><InputNumber style={{ width: '100%' }} /></Form.Item>
                        <Form.Item name="borrowingsOutsideIndia" label="Foreign Borrowings"><InputNumber style={{ width: '100%' }} /></Form.Item>
                    </div>
                </>
            );
        }
        if (modalType === 'details') {
            return (
                <>
                    <Form.Item name="borrowingSecurityType" label="Security Type" rules={[{ required: true }]}>
                        <Select>
                            <Option value="SECURED">SECURED</Option>
                            <Option value="UNSECURED">UNSECURED</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="borrowingType" label="Borrowing Type" rules={[{ required: true }]}>
                        <Select showSearch placeholder="Select Type">
                            <Option value="TERM_LOAN">TERM_LOAN</Option>
                            <Option value="TERM_LOAN_FROM_BANKS">TERM_LOAN_FROM_BANKS</Option>
                            <Option value="TERM_LOAN_FROM_OTHER_BANKS">TERM_LOAN_FROM_OTHER_BANKS</Option>
                            <Option value="TERM_LOAN_FROM_FINANCIAL_INSTITUTIONS">TERM_LOAN_FROM_FINANCIAL_INSTITUTIONS</Option>
                            <Option value="TERM_LOAN_FROM_NHB">TERM_LOAN_FROM_NHB</Option>
                            <Option value="EXTERNAL_COMMERCIAL_BORROWINGS_ECB">EXTERNAL_COMMERCIAL_BORROWINGS_ECB</Option>
                            <Option value="NON_CONVERTIBLE_DEBENTURES_NCDS">NON_CONVERTIBLE_DEBENTURES_NCDS</Option>
                            <Option value="CASH_CREDIT_FROM_BANKS">CASH_CREDIT_FROM_BANKS</Option>
                            <Option value="WORKING_CAPITAL">WORKING_CAPITAL</Option>
                            <Option value="OTHER">OTHER</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="totalAmount" label="Total Amount"><InputNumber style={{ width: '100%' }} /></Form.Item>
                    <Form.Item name="tenure" label="Tenure"><Input /></Form.Item>
                    <Form.Item name="rateOfInterest" label="Interest Rate"><Input /></Form.Item>
                    <Form.Item name="repaymentDetails" label="Repayment Details"><Input.TextArea /></Form.Item>
                </>
            );
        }
        if (modalType === 'ncd' || modalType === 'cp') {
            return (
                <>
                    <Form.Item name="borrowingSecurityType" label="Security Type" rules={[{ required: true }]}>
                        <Select>
                            <Option value="SECURED">SECURED</Option>
                            <Option value="UNSECURED">UNSECURED</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="seriesDetails" label="Series Details"><Input /></Form.Item>
                    <Form.Item name="dateOfAllotment" label="Allotment Date"><Input placeholder="YYYY-MM-DD" /></Form.Item>
                    <Form.Item name="amount" label="Amount"><InputNumber style={{ width: '100%' }} /></Form.Item>
                    <Form.Item name="interestRate" label="Interest Rate"><Input /></Form.Item>
                    <Form.Item name="dateOfRedemption" label="Redemption Date"><Input placeholder="YYYY-MM-DD" /></Form.Item>
                    {modalType === 'ncd' && <Form.Item name="numberOfNcds" label="Number of NCDs"><InputNumber style={{ width: '100%' }} /></Form.Item>}
                    {/* Add face value if needed */}
                </>
            );
        }
    };


    // ================== REVIEW MODAL HANDLERS ==================

    const [reviewModalVisible, setReviewModalVisible] = useState(false);
    const [extractedData, setExtractedData] = useState(null);
    const [isReviewEdit, setIsReviewEdit] = useState(false);
    const [reviewEditKey, setReviewEditKey] = useState(null); // identifier for row being edited in review

    const openReviewEditModal = (type, record, reviewKey) => {
        setIsReviewEdit(true);
        setReviewEditKey(reviewKey);
        openModal(type, record);

        // Map snake_case to camelCase for the form if it's from extraction
        const camelRecord = {};
        Object.keys(record).forEach(key => {
            const camelKey = key.replace(/([-_][a-z])/g, group =>
                group.toUpperCase().replace('-', '').replace('_', '')
            );
            camelRecord[camelKey] = record[key];
        });

        // Special mapping for details type where category is the borrowingType
        if (type === 'details' && record.category) {
            camelRecord.borrowingType = record.category.toUpperCase();
        }

        // Handle the security type specially if it's nested
        if (record.id?.borrowingSecurityType) {
            camelRecord.borrowingSecurityType = record.id.borrowingSecurityType;
        } else if (record.security) {
            camelRecord.borrowingSecurityType = record.security;
        }

        form.setFieldsValue(camelRecord);
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();

            if (isReviewEdit) {
                updateExtractedDataLocally(values);
                setModalVisible(false);
                setIsReviewEdit(false);
                return;
            }

            let endpoint = '';
            let payload = [];
            const objectToSave = { ...values };
            if (editingRecord && editingRecord.id) {
                objectToSave.id = editingRecord.id;
            }
            const issuerEntity = { issuer: selectedIssuer };

            if (modalType === 'borrowing') {
                const entity = {
                    id: {
                        issuer: selectedIssuer,
                        borrowingSecurityType: values.borrowingSecurityType
                    },
                    ...values
                };
                delete entity.issuer;
                delete entity.borrowingSecurityType;
                endpoint = `${baseUrl}/borrowing/issuer/${selectedIssuer}/borrowings`;
                payload = [entity];
            } else {
                objectToSave.issuer = issuerEntity;
                if (modalType === 'details') {
                    endpoint = `${baseUrl}/borrowing/issuer/${selectedIssuer}/details`;
                } else if (modalType === 'ncd') {
                    endpoint = `${baseUrl}/borrowing/issuer/${selectedIssuer}/ncds`;
                } else if (modalType === 'cp') {
                    endpoint = `${baseUrl}/borrowing/issuer/${selectedIssuer}/commercial-papers`;
                }
                payload = [objectToSave];
            }

            await apiClient.post(endpoint, payload, {
                headers: authHeaders
            });

            message.success("Saved successfully");
            setModalVisible(false);
            fetchBorrowingData(selectedIssuer);
        } catch (error) {
            console.error("Save error:", error);
            const errMsg =
                error.response?.data?.message ||
                error.response?.data ||
                "Error saving data";
            message.error(errMsg);
        }
    };

    const handleBulkSave = async () => {
        if (!extractedData) return;

        // Helper to convert snake_case to camelCase AND clean numeric strings (remove commas, -, etc)
        const cleanAndCamel = (obj) => {
            if (Array.isArray(obj)) return obj.map(v => cleanAndCamel(v));
            if (obj != null && obj.constructor === Object) {
                return Object.keys(obj).reduce((acc, key) => {
                    const camelKey = key.replace(/([-_][a-z])/g, group =>
                        group.toUpperCase().replace('-', '').replace('_', '')
                    );
                    acc[camelKey] = cleanAndCamel(obj[key]);
                    return acc;
                }, {});
            }

            if (typeof obj === 'string') {
                const val = obj.trim();
                // Treat -, nil, NA, empty as null
                if (['-', '', 'nil', 'nil ', 'NA', 'N.A.'].includes(val)) return null;

                // If it's a string that looks like a number with symbols (e.g. "42,656", "₹ 1,000", "9.5%*")
                // but exclude things with letters (like Series Details)
                if (/^[\d,.\-₹\s%*]+$/.test(val)) {
                    const cleaned = val.replace(/[₹%*,\s]/g, '');
                    // Only return cleaned if it's a valid number format, otherwise return original
                    if (/^-?\d+(\.\d+)?$/.test(cleaned)) {
                        return cleaned;
                    }
                }
            }
            return obj;
        };

        try {
            const issuerObj = { issuer: selectedIssuer };

            // Helper to pivot borrowingDetails from {secured: {CAT:[]}} to {CAT: {secured:[]}}
            const transformDetails = (details) => {
                const transformed = {};
                const process = (subData, securityType) => {
                    if (!subData) return;
                    Object.keys(subData).forEach(cat => {
                        const enumKey = cat.toUpperCase();
                        if (!transformed[enumKey]) transformed[enumKey] = { secured: [], unsecured: [] };
                        const cleanedRows = (subData[cat] || []).map(row => ({
                            ...cleanAndCamel(row),
                            issuer: issuerObj,
                            borrowingSecurityType: securityType,
                            borrowingType: enumKey
                        }));
                        transformed[enumKey][securityType.toLowerCase()] = cleanedRows;
                    });
                };
                process(details.secured, 'SECURED');
                process(details.unsecured, 'UNSECURED');
                return transformed;
            };

            const payload = {
                issuer: selectedIssuer,
                borrowings: cleanAndCamel(extractedData.borrowings).map(b => ({
                    ...b,
                    id: { ...b.id, issuer: selectedIssuer },
                    issuer: issuerObj
                })),
                borrowingDetails: transformDetails(extractedData.borrowingDetails),
                ncds: {
                    secured: (cleanAndCamel(extractedData.ncds?.secured) || []).map(r => ({ ...r, issuer: issuerObj, borrowingSecurityType: 'SECURED' })),
                    unsecured: (cleanAndCamel(extractedData.ncds?.unsecured) || []).map(r => ({ ...r, issuer: issuerObj, borrowingSecurityType: 'UNSECURED' }))
                },
                commercialPapers: {
                    secured: (cleanAndCamel(extractedData.commercialPapers?.secured) || []).map(r => ({ ...r, issuer: issuerObj, borrowingSecurityType: 'SECURED' })),
                    unsecured: (cleanAndCamel(extractedData.commercialPapers?.unsecured) || []).map(r => ({ ...r, issuer: issuerObj, borrowingSecurityType: 'UNSECURED' }))
                }
            };

            await apiClient.post(
                `${baseUrl}/borrowing/issuer/${encodeURIComponent(
                    selectedIssuer
                )}/all`,
                payload,
                { headers: authHeaders }
            );

            message.success("All borrowing data saved successfully!");
            setReviewModalVisible(false);
            setExtractedData(null);
            fetchBorrowingData(selectedIssuer);
        } catch (error) {
            console.error("Bulk save error:", error);
            const errMsg =
                error.response?.data?.message ||
                error.response?.data ||
                "Error saving extracted data";
            message.error(errMsg);
        }
    };

    const updateExtractedDataLocally = (values) => {
        const { type, subType, index, category } = reviewEditKey;
        const newData = { ...extractedData };

        // Helper to convert camelCase from form back to snake_case for local state
        const toSnake = (obj) => {
            const snaked = {};
            Object.keys(obj).forEach(key => {
                const snakeKey = key.replace(/[A-Z]/g, match => `_${match.toLowerCase()}`);
                snaked[snakeKey] = obj[key];
            });
            return snaked;
        };

        const snakedValues = toSnake(values);

        if (type === 'borrowing') {
            newData.borrowings = [...newData.borrowings];
            const idx = newData.borrowings.findIndex(b => b.id.borrowingSecurityType === values.borrowingSecurityType);
            if (idx !== -1) {
                newData.borrowings[idx] = { ...newData.borrowings[idx], ...snakedValues };
            }
        } else if (type === 'details') {
            newData.borrowingDetails = { ...newData.borrowingDetails };
            newData.borrowingDetails[subType] = { ...newData.borrowingDetails[subType] };
            newData.borrowingDetails[subType][category] = [...newData.borrowingDetails[subType][category]];
            newData.borrowingDetails[subType][category][index] = {
                ...newData.borrowingDetails[subType][category][index],
                ...snakedValues
            };
        } else if (type === 'ncd') {
            newData.ncds = { ...newData.ncds };
            newData.ncds[subType] = [...newData.ncds[subType]];
            newData.ncds[subType][index] = { ...newData.ncds[subType][index], ...snakedValues };
        } else if (type === 'cp') {
            newData.commercialPapers = { ...newData.commercialPapers };
            newData.commercialPapers[subType] = [...newData.commercialPapers[subType]];
            newData.commercialPapers[subType][index] = { ...newData.commercialPapers[subType][index], ...snakedValues };
        }

        setExtractedData(newData);
        message.success("Record updated locally for review.");
    };

    // Updated PDF Handler
    const handleUploadAndExtractPDF = () => {
        if (!selectedIssuer) {
            message.warning("Please select an issuer before uploading PDF");
            return;
        }

        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pdf';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            setExtractionLoading(true);
            setReviewModalVisible(true);
            setExtractedData(null); // Clear previous

            try {
                const formData = new FormData();
                formData.append('file', file);

                const response = await fetch(`${pocBotsUrl}/api/extract-borrowings`, {
                    method: 'POST',
                    body: formData,
                });

                if (response.ok) {
                    const result = await response.json();
                    if (result.status === 'success' && result.data) {
                        // result.data should be the composite object from Python Bot
                        // Map Python keys to Frontend/Java DTO keys
                        const raw = result.data;
                        const mappedData = {
                            borrowings: [
                                {
                                    id: { borrowingSecurityType: 'SECURED' },
                                    ...raw.borrowings?.secured,
                                    borrowings_within_india: raw.borrowings?.borrowings_within_india,
                                    borrowings_outside_india: raw.borrowings?.borrowings_outside_india
                                },
                                {
                                    id: { borrowingSecurityType: 'UNSECURED' },
                                    ...raw.borrowings?.unsecured,
                                    borrowings_within_india: raw.borrowings?.borrowings_within_india,
                                    borrowings_outside_india: raw.borrowings?.borrowings_outside_india
                                }
                            ],
                            borrowingDetails: raw.detailed_data || {},
                            ncds: raw.ncd_data || { secured: [], unsecured: [] },
                            commercialPapers: raw.commercial_papers_data || { secured: [], unsecured: [] }
                        };

                        setExtractedData(mappedData);
                        message.success("Extraction complete.");
                    } else {
                        message.warning("Extraction returned no data.");
                    }
                } else {
                    // Fallback/Demo Mock Data
                    console.log("Using Mock Data for Review");
                    const mockData = {
                        borrowings: [
                            { id: { borrowingSecurityType: 'SECURED' }, term_loan_from_banks: 1000 },
                            { id: { borrowingSecurityType: 'UNSECURED' }, term_loan_from_banks: 500 }
                        ],
                        borrowingDetails: {},
                        ncds: { secured: [], unsecured: [] },
                        commercialPapers: { secured: [], unsecured: [] }
                    };
                    setExtractedData(mockData);
                }

            } catch (error) {
                console.error("Extraction error:", error);
                message.error("Failed to extract data");
            } finally {
                setExtractionLoading(false);
            }
        };
        input.click();
    };


    return (
        <div className="admin-page">
            <div className="admin-header-banner">
                <div className="hero-content">
                    <h1 className="hero-title-news">Issuer Borrowing Management</h1>
                </div>
            </div>

            <Card className="admin-main-card">
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="bottom">
                    <Col xs={24} md={10}>
                        <label className="admin-filter-label">Issuer</label>
                        <Select
                            showSearch
                            style={{ width: "100%" }}
                            placeholder="Select Issuer"
                            optionFilterProp="children"
                            onChange={handleIssuerChange}
                            value={selectedIssuer}
                            filterOption={(input, option) =>
                                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {issuerOptions.map((issuer) => (
                                <Option key={issuer.issuer} value={issuer.issuer}>
                                    {issuer.issuer}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} md={6}>
                        <Button
                            type="primary"
                            icon={<UploadOutlined />}
                            onClick={handleUploadAndExtractPDF}
                            disabled={!selectedIssuer}
                            loading={extractionLoading}
                        >
                            Upload Borrowings PDF
                        </Button>
                    </Col>
                </Row>

                {selectedIssuer && (
                    <Tabs defaultActiveKey="1">
                        <TabPane tab="Summary Overview" key="1">
                            {renderGeneralBorrowings()}
                        </TabPane>
                        <TabPane tab="Breakup Details" key="2">
                            {renderDetails()}
                        </TabPane>
                        <TabPane tab="NCDs" key="3">
                            {renderInstruments('ncd')}
                        </TabPane>
                        <TabPane tab="Commercial Papers" key="4">
                            {renderInstruments('cp')}
                        </TabPane>
                    </Tabs>
                )}
            </Card>

            <Modal
                title={editingRecord ? "Edit Record" : "Add Record"}
                open={modalVisible}
                onOk={handleModalOk}
                onCancel={() => setModalVisible(false)}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    {renderFormContent()}
                </Form>
            </Modal>

            {/* Review Modal */}
            <Modal
                title="AI Borrowing Data Review"
                open={reviewModalVisible}
                onOk={handleBulkSave}
                onCancel={() => setReviewModalVisible(false)}
                width={1200}
                okText="Push to Database"
                okButtonProps={{ disabled: extractionLoading || !extractedData }}
                cancelButtonProps={{ disabled: extractionLoading }}
            >
                {extractionLoading ? (
                    <div style={{ padding: '100px 0', textAlign: 'center' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 24, fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>
                            Our AI is analyzing the PDF...
                        </div>
                        <div style={{ color: '#888', marginTop: 8 }}>
                            Finding borrowing tables, NCD schedules, and Commercial Paper details.
                        </div>
                    </div>
                ) : extractedData ? (
                    <Tabs defaultActiveKey="1">

                        <TabPane tab="Summary Overview" key="1">
                            <Table
                                dataSource={extractedData.borrowings || []}
                                columns={[
                                    { title: 'Security', dataIndex: ['id', 'borrowingSecurityType'], fixed: 'left', width: 120 },
                                    { title: 'Term Loan (Banks)', dataIndex: 'term_loan_from_banks', render: (v) => v || '-' },
                                    { title: 'Term Loan (Other Banks)', dataIndex: 'term_loan_from_other_banks', render: (v) => v || '-' },
                                    { title: 'Term Loan (Fin. Inst.)', dataIndex: 'term_loan_from_financial_institutions', render: (v) => v || '-' },
                                    { title: 'Term Loan (Foreign Banks)', dataIndex: 'term_loan_from_foreign_banks', render: (v) => v || '-' },
                                    { title: 'Term Loan (Foreign FI)', dataIndex: 'term_loan_from_foreign_financial_institutions', render: (v) => v || '-' },
                                    { title: 'ECB', dataIndex: 'external_commercial_borrowings_ecb', render: (v) => v || '-' },
                                    { title: 'Other Loans', dataIndex: 'other_loans', render: (v) => v || '-' },
                                    { title: 'Term Loan (NHB)', dataIndex: 'term_loan_from_nhb', render: (v) => v || '-' },
                                    { title: 'NCDs', dataIndex: 'non_convertible_debentures_ncds', render: (v) => v || '-' },
                                    { title: 'Working Capital Loan', dataIndex: 'loans_repayable_on_demand_working_capital_demand_loan', render: (v) => v || '-' },
                                    { title: 'Loans Related Parties', dataIndex: 'loans_from_related_parties', render: (v) => v || '-' },
                                    { title: 'Cash Credit', dataIndex: 'cash_credit_from_banks', render: (v) => v || '-' },
                                    { title: 'Bank Overdraft', dataIndex: 'bank_overdraft', render: (v) => v || '-' },
                                    { title: 'Domestic Borrowings', dataIndex: 'borrowings_within_india', render: (v) => v || '-' },
                                    { title: 'Foreign Borrowings', dataIndex: 'borrowings_outside_india', render: (v) => v || '-' },
                                    {
                                        title: 'Action',
                                        key: 'action',
                                        fixed: 'right',
                                        width: 70,
                                        render: (_, record) => (
                                            <Button
                                                icon={<EditOutlined />}
                                                onClick={() => openReviewEditModal('borrowing', record, { type: 'borrowing' })}
                                            />
                                        )
                                    }
                                ]}
                                rowKey={(r) => r.id?.borrowingSecurityType || Math.random()}
                                pagination={{ pageSize: 5 }}
                                scroll={{ x: 3000 }}
                            />
                            <div style={{ marginTop: 16 }}>
                                <Tag color="blue">Note: The data above reflects extracted values from the PDF. They will be saved as new records for the selected issuer.</Tag>
                            </div>
                        </TabPane>
                        <TabPane tab="Breakup Details" key="2">
                            <Table
                                dataSource={[
                                    ...Object.keys(extractedData.borrowingDetails?.secured || {}).flatMap(cat =>
                                        (extractedData.borrowingDetails.secured[cat] || []).map((row, idx) => ({ ...row, category: cat, security: 'SECURED', index: idx }))
                                    ),
                                    ...Object.keys(extractedData.borrowingDetails?.unsecured || {}).flatMap(cat =>
                                        (extractedData.borrowingDetails.unsecured[cat] || []).map((row, idx) => ({ ...row, category: cat, security: 'UNSECURED', index: idx }))
                                    )
                                ]}
                                columns={[
                                    { title: 'Security', dataIndex: 'security', width: 100 },
                                    { title: 'Category', dataIndex: 'category', render: (v) => v?.replace(/_/g, ' ').toUpperCase() },
                                    { title: 'Amount', dataIndex: 'total_amount' },
                                    { title: 'Tenure', dataIndex: 'tenure' },
                                    { title: 'Rate', dataIndex: 'rate_of_interest' },
                                    {
                                        title: 'Action',
                                        key: 'action',
                                        render: (_, record) => (
                                            <Button
                                                icon={<EditOutlined />}
                                                onClick={() => openReviewEditModal('details', record, {
                                                    type: 'details',
                                                    subType: record.security.toLowerCase(),
                                                    category: record.category,
                                                    index: record.index
                                                })}
                                            />
                                        )
                                    }
                                ]}
                                pagination={{ pageSize: 10 }}
                                scroll={{ x: 1000 }}
                            />
                        </TabPane>
                        <TabPane tab="NCDs" key="3">
                            <div style={{ marginBottom: 24 }}>
                                <h4>Secured NCDs</h4>
                                <Table
                                    dataSource={extractedData.ncds?.secured || []}
                                    columns={[
                                        { title: 'Series', dataIndex: 'series_details' },
                                        { title: 'Amount', dataIndex: 'amount' },
                                        { title: 'Rate', dataIndex: 'interest_rate' },
                                        {
                                            title: 'Action',
                                            key: 'action',
                                            render: (_, record, idx) => (
                                                <Button
                                                    icon={<EditOutlined />}
                                                    onClick={() => openReviewEditModal('ncd', record, { type: 'ncd', subType: 'secured', index: idx })}
                                                />
                                            )
                                        }
                                    ]}
                                    rowKey={(r, i) => `sec-ncd-${i}`}
                                    pagination={{ pageSize: 5 }}
                                />
                            </div>
                            <div>
                                <h4>Unsecured NCDs</h4>
                                <Table
                                    dataSource={extractedData.ncds?.unsecured || []}
                                    columns={[
                                        { title: 'Series', dataIndex: 'series_details' },
                                        { title: 'Amount', dataIndex: 'amount' },
                                        { title: 'Rate', dataIndex: 'interest_rate' },
                                        {
                                            title: 'Action',
                                            key: 'action',
                                            render: (_, record, idx) => (
                                                <Button
                                                    icon={<EditOutlined />}
                                                    onClick={() => openReviewEditModal('ncd', record, { type: 'ncd', subType: 'unsecured', index: idx })}
                                                />
                                            )
                                        }
                                    ]}
                                    rowKey={(r, i) => `unsec-ncd-${i}`}
                                    pagination={{ pageSize: 5 }}
                                />
                            </div>
                        </TabPane>
                        <TabPane tab="Commercial Papers" key="4">
                            <div style={{ marginBottom: 24 }}>
                                <h4>Secured Commercial Papers</h4>
                                <Table
                                    dataSource={extractedData.commercialPapers?.secured || []}
                                    columns={[
                                        { title: 'Series', dataIndex: 'series_details' },
                                        { title: 'Amount', dataIndex: 'amount' },
                                        { title: 'Rate', dataIndex: 'interest_rate' },
                                        {
                                            title: 'Action',
                                            key: 'action',
                                            render: (_, record, idx) => (
                                                <Button
                                                    icon={<EditOutlined />}
                                                    onClick={() => openReviewEditModal('cp', record, { type: 'cp', subType: 'secured', index: idx })}
                                                />
                                            )
                                        }
                                    ]}
                                    rowKey={(r, i) => `sec-cp-${i}`}
                                    pagination={{ pageSize: 5 }}
                                />
                            </div>
                            <div>
                                <h4>Unsecured Commercial Papers</h4>
                                <Table
                                    dataSource={extractedData.commercialPapers?.unsecured || []}
                                    columns={[
                                        { title: 'Series', dataIndex: 'series_details' },
                                        { title: 'Amount', dataIndex: 'amount' },
                                        { title: 'Rate', dataIndex: 'interest_rate' },
                                        {
                                            title: 'Action',
                                            key: 'action',
                                            render: (_, record, idx) => (
                                                <Button
                                                    icon={<EditOutlined />}
                                                    onClick={() => openReviewEditModal('cp', record, { type: 'cp', subType: 'unsecured', index: idx })}
                                                />
                                            )
                                        }
                                    ]}
                                    rowKey={(r, i) => `unsec-cp-${i}`}
                                    pagination={{ pageSize: 5 }}
                                />
                            </div>
                        </TabPane>
                    </Tabs>
                ) : <p>No data extracted</p>}
            </Modal>
        </div>
    );
};

export default IssuerBorrowingAdmin;
